// Generic renderer for JSON-defined social-post templates.
//
// Reads a JsonTemplate config + the AI-filled SocialPostSlots and
// composes a 1080×1080 canvas. Parent (StepSocialFinal) wraps this in
// a scaled container — the renderer always produces a pixel-perfect
// 1080×1080 div so html-to-image PNG export is consistent regardless
// of preview scale.
//
// Architecture:
//   - Outer .json-social-post fixes 1080×1080
//   - Background-color fill (from canvas.backgroundColor)
//   - For each element in config.elements (in order): absolute-positioned
//     div using the element's x/y/width and type-specific styling
//   - Product image rendered last so it's above the background fill but
//     below any decorative elements above it in the array
//
// Z-ordering: array order = visual stacking (first = back, last = front).
// productSlot has implicit z between background and elements[0]. To
// place an element BEHIND the product, that's not currently supported
// in v1 — workaround: layer the product as an image element manually
// and skip the productSlot. Future enhancement.

import type {
  JsonTemplate,
  TemplateElement,
  TextElement,
  ButtonElement,
  ShapeElement,
  ImageElement,
  ProductSlotElement,
  LogoSlotElement,
  StackElement,
  ShadowSpec,
  SocialPostSlots,
} from './types';

/** Map our align tokens to CSS align-items / align-self values. */
function cssAlign(a: 'start' | 'center' | 'end' | 'stretch' | undefined): string | undefined {
  if (!a) return undefined;
  if (a === 'start') return 'flex-start';
  if (a === 'end') return 'flex-end';
  return a; // center | stretch
}

/** Map our justify tokens to CSS justify-content values. */
function cssJustify(
  j: 'start' | 'center' | 'end' | 'space-between' | undefined,
): string | undefined {
  if (!j) return undefined;
  if (j === 'start') return 'flex-start';
  if (j === 'end') return 'flex-end';
  return j; // center | space-between
}

/** CSS box-shadow string for boxed elements (shapes/buttons/images/
 *  product-slots). Supports spread. */
function boxShadowCss(s: ShadowSpec | undefined): string | undefined {
  if (!s) return undefined;
  return `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread ?? 0}px ${s.color}`;
}

/** CSS text-shadow string for text elements (no spread in text-shadow). */
function textShadowCss(s: ShadowSpec | undefined): string | undefined {
  if (!s) return undefined;
  return `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.color}`;
}

/** Convert a #rrggbb hex to rgba() at the given alpha (for the glass fill).
 *  Returns the input unchanged if it isn't a 6-digit hex. */
function hexWithAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

interface JsonSocialPostProps {
  config: JsonTemplate;
  slots: SocialPostSlots;
  /** Data URL or blob URL for the user's product photo. Empty string →
   *  placeholder rendered in the product slot. */
  productImageUrl: string;
  /** When true, staticText wins over slot value for text + button
   *  elements. Used by the visual builder so user-typed copy in the
   *  inspector renders in the canvas instead of being replaced by
   *  SAMPLE_SLOTS data. Production (StepSocialFinal) leaves this
   *  unset / false so AI-filled slot content takes priority. */
  preferStatic?: boolean;
  /** Scene-merge result: the product AI-composited into the background
   *  scene. When set, this image is used as the full-canvas background
   *  (the original `*-background` element is suppressed), and the
   *  product-slot stays empty since the product is already in the scene. */
  mergedSceneUrl?: string | null;
  /** The brand logo (from My Brand / /api/brand). Rendered into any
   *  `logo-slot` elements. Null/empty → logo slots stay transparent. */
  brandLogoUrl?: string | null;
}

/** True once web fonts have finished loading. Auto-shrink text
 *  measurement (ctx.measureText) is only accurate once the real fonts
 *  are available — before that, fallback-font metrics miscount lines
 *  and the shrink is skipped, letting AI copy overflow. Flipping this
 *  after document.fonts.ready forces a re-measure with the right fonts. */
function useFontsReady(): boolean {
  const [ready, setReady] = useState(
    () =>
      typeof document === 'undefined' ||
      !document.fonts ||
      document.fonts.status === 'loaded',
  );
  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ready]);
  return ready;
}

export default function JsonSocialPost({
  config,
  slots,
  productImageUrl,
  preferStatic = false,
  mergedSceneUrl = null,
  brandLogoUrl = null,
}: JsonSocialPostProps) {
  const { canvas } = config;
  const fontsReady = useFontsReady();

  // Scene-merge: the AI-merged image (product in scene) replaces the
  // background. Suppress the original `*-background` image element so it
  // doesn't cover the merge, and don't paste the product into the slot
  // (it's already in the scene).
  const isSceneMergeTemplate = config.composeMode === 'scene-merge';
  const sceneMergeActive = !!mergedSceneUrl;
  const elements = sceneMergeActive
    ? config.elements.filter((el) => !(el.type === 'image' && el.id.endsWith('-background')))
    : config.elements;
  // For a scene-merge template the product lives IN the merged scene —
  // never paste the raw product into the slot. While the merge is
  // in-flight (or if it fails) the slot stays empty and the original
  // scene shows, instead of flashing the ugly raw white-bg product.
  const effectiveProductUrl = sceneMergeActive || isSceneMergeTemplate ? '' : productImageUrl;
  const bgImage = sceneMergeActive ? mergedSceneUrl : canvas.backgroundImage;

  return (
    // The root is absolutely sized — parent scales via CSS transform.
    // Inline styles (not Tailwind) because layout is data-driven; this
    // is the one place in the codebase where it makes sense to forgo
    // utility classes.
    <div
      className="json-social-post"
      style={{
        position: 'relative',
        width: canvas.width,
        height: canvas.height,
        backgroundColor: canvas.backgroundColor,
        overflow: 'hidden',
      }}
    >
      {/* Optional canvas background image — sits ABOVE the bg color but
          BELOW the product slot and elements. Use for lifestyle scenes,
          textures, gradient backgrounds. Configured per-template via
          the schema's canvas.backgroundImage field. */}
      {bgImage && (
        <img
          src={bgImage}
          alt=""
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: canvas.width,
            height: canvas.height,
            objectFit: canvas.backgroundImageFit ?? 'cover',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      )}

      {/* Layout elements. A `stackOnPrevious` chain (an anchor element +
          the run of stackOnPrevious siblings after it) renders as a REAL
          CSS flex column anchored at the anchor's x/y — the browser does
          the reflow, so the editor and the headless bake (both Chromium)
          lay out identically. NO canvas measureText anywhere. Everything
          else is a standalone absolute element — which is every Social
          Post design element (none use stackOnPrevious), so those are
          byte-identical to before. */}
      {renderElementRuns(elements, slots, effectiveProductUrl, preferStatic, fontsReady, brandLogoUrl)}
    </div>
  );
}

/** Group elements into runs and render them. A run is an anchor element
 *  followed by consecutive `stackOnPrevious` siblings. Singleton runs
 *  render absolutely at their literal x/y. Multi-element runs render as
 *  an absolutely-positioned flex column at the anchor's x/y, with each
 *  member in normal flow (inFlow=true) so the browser stacks them by
 *  their ACTUAL rendered height — no measurement, no drift. */
function renderElementRuns(
  elements: TemplateElement[],
  slots: SocialPostSlots,
  productImageUrl: string,
  preferStatic: boolean,
  fontsReady: boolean,
  brandLogoUrl: string | null,
): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const n = elements.length;
  let i = 0;
  while (i < n) {
    let end = i;
    while (end + 1 < n && elements[end + 1].stackOnPrevious) end++;
    if (end > i) {
      const anchor = elements[i];
      // Uniform gap between chain members (templates use one gap per chain).
      const gap = elements[i + 1]?.stackGap ?? 0;
      out.push(
        <div
          key={`chain-${anchor.id}`}
          style={{
            position: 'absolute',
            left: anchor.x,
            top: anchor.y,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap,
          }}
        >
          {elements.slice(i, end + 1).map((el) => (
            // Hug controls Y only — the flex column flows members vertically.
            // X stays PER-ELEMENT: offset each member by its own x relative to
            // the anchor so a hugged element keeps its authored horizontal
            // position instead of snapping to the anchor's x.
            <div key={el.id} style={{ alignSelf: 'flex-start', marginLeft: el.x - anchor.x }}>
              {renderElement(el, slots, productImageUrl, preferStatic, fontsReady, true, brandLogoUrl)}
            </div>
          ))}
        </div>,
      );
      i = end + 1;
    } else {
      out.push(
        renderElement(elements[i], slots, productImageUrl, preferStatic, fontsReady, false, brandLogoUrl),
      );
      i++;
    }
  }
  return out;
}

// ---------- Element renderers ----------

function renderElement(
  el: TemplateElement,
  slots: SocialPostSlots,
  productImageUrl: string,
  preferStatic: boolean,
  fontsReady: boolean,
  // True when this element is a flow child of a stack — render it
  // relatively (no absolute left/top) so it participates in the layout.
  inFlow = false,
  brandLogoUrl: string | null = null,
) {
  switch (el.type) {
    case 'text':
      return <RenderText key={el.id} el={el} slots={slots} preferStatic={preferStatic} fontsReady={fontsReady} inFlow={inFlow} />;
    case 'button':
      return <RenderButton key={el.id} el={el} slots={slots} preferStatic={preferStatic} inFlow={inFlow} />;
    case 'shape':
      return <RenderShape key={el.id} el={el} inFlow={inFlow} />;
    case 'image':
      return <RenderImage key={el.id} el={el} inFlow={inFlow} />;
    case 'product-slot':
      return <RenderProductSlot key={el.id} el={el} productImageUrl={productImageUrl} inFlow={inFlow} />;
    case 'logo-slot':
      return <RenderLogoSlot key={el.id} el={el} brandLogoUrl={brandLogoUrl} inFlow={inFlow} />;
    case 'stack':
      return (
        <RenderStack
          key={el.id}
          el={el}
          slots={slots}
          productImageUrl={productImageUrl}
          preferStatic={preferStatic}
          fontsReady={fontsReady}
          inFlow={inFlow}
          brandLogoUrl={brandLogoUrl}
        />
      );
    default: {
      // TypeScript exhaustiveness check — if a new element type is
      // added to the union and not handled here, this errors at compile.
      const _exhaustive: never = el;
      void _exhaustive;
      return null;
    }
  }
}

/** Resolve an element's content from the fallback chain. Order depends
 *  on `preferStatic` — true means staticText > slot (builder mode),
 *  false means slot > staticText (production mode). */
function resolveContent(
  slotKey: keyof SocialPostSlots | undefined,
  staticText: string | undefined,
  slots: SocialPostSlots,
  preferStatic: boolean,
): string {
  const slotValue = slotKey ? slots[slotKey] : undefined;
  if (preferStatic) {
    return (staticText || slotValue) ?? '';
  }
  return (slotValue || staticText) ?? '';
}

/** Does the content contain HTML tags? Used to auto-render with
 *  dangerouslySetInnerHTML when an AI fill comes back with <em>...</em>
 *  even though the element doesn't have explicit allowHtml=true. This
 *  removes a footgun: previously a JSON template's headline element
 *  would render the literal string "Wear <em>hydration</em> as protection"
 *  unless the author remembered to flip the allowHtml toggle. Now any
 *  content with an HTML-looking tag is parsed regardless. The explicit
 *  allowHtml flag still works as an override for plain-text content
 *  that should be parsed. */
function looksLikeHtml(s: string): boolean {
  return /<\/?[a-z][^>]*>/i.test(s);
}

/** Strip HTML tags for text measurement — Canvas measureText doesn't
 *  understand markup, but the tags don't add visible width. Keeps the
 *  fit-size estimate accurate for content like "Wear <em>hydration</em>"
 *  by measuring "Wear hydration". */
function stripHtmlForMeasurement(s: string): string {
  return s.replace(/<\/?[a-z][^>]*>/gi, '');
}

/** Shared offscreen canvas + ctx for text measurement. Lazily created
 *  on first call. measureText needs the same font set we'd use in the
 *  DOM render — same family, weight, size — to give accurate widths. */
let _measureCtx: CanvasRenderingContext2D | null = null;
function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (_measureCtx) return _measureCtx;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  _measureCtx = canvas.getContext('2d');
  return _measureCtx;
}

/** Greedy word-wrap to width. Returns one entry per visual line.
 *  Mirrors the legacy composeRender.wrapTextToWidth behavior. A word
 *  wider than maxWidth is split into char-chunks that each fit, so the
 *  LINE COUNT matches what the browser does (overflow-wrap / hyphen
 *  break) — otherwise an unbreakable word like "PHOTO-RESILIENCE" is
 *  measured as 1 line but renders as 2+, and the maxLines auto-shrink
 *  under-counts → text overflows to extra lines. */
function wrapToWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  letterSpacing = 0,
): string[] {
  if (!text) return [];
  // CSS letter-spacing widens text (~the spacing added between each glyph);
  // canvas measureText ignores it, so add it back or the wrapped line count
  // under-counts and the box/shrink end up too small for letter-spaced copy.
  const measure = (s: string) =>
    ctx.measureText(s).width + Math.max(0, s.length - 1) * letterSpacing;
  // A single word wider than the box → char-chunk it so the line count
  // mirrors the browser's intra-word break (hyphen / overflow-wrap).
  const breakLongWord = (word: string): string[] => {
    if (measure(word) <= maxWidth) return [word];
    const chunks: string[] = [];
    let cur = '';
    for (const ch of word) {
      if (cur && measure(cur + ch) > maxWidth) {
        chunks.push(cur);
        cur = ch;
      } else {
        cur += ch;
      }
    }
    if (cur) chunks.push(cur);
    return chunks;
  };
  // Split on whitespace but preserve explicit \n as hard line breaks
  // (used by bullet_list and any author-typed multi-line static text).
  const paragraphs = text.split(/\r?\n/);
  const lines: string[] = [];
  for (const para of paragraphs) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      continue;
    }
    let current = '';
    for (const word of words) {
      const test = current ? `${current} ${word}` : word;
      if (measure(test) <= maxWidth) {
        current = test;
      } else {
        if (current) {
          lines.push(current);
          current = '';
        }
        // The word didn't fit the current line; if it's also wider than the
        // whole box, break it so each piece counts as its own line.
        const chunks = breakLongWord(word);
        for (let i = 0; i < chunks.length - 1; i++) lines.push(chunks[i]);
        current = chunks[chunks.length - 1];
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

/** Iteratively shrink fontSize until the wrapped + measured text fits
 *  within the element's bounding box (and maxLines, if set). Returns
 *  the largest size that fits, floored at 40% of the authored size
 *  (so text never disappears even with absurdly long AI output).
 *
 *  Strategy: linear-ish shrink (8% per step) — matches the legacy
 *  preset system (composeRender.getEffectiveFontSize). 8% is gentle
 *  enough to find a tight fit without spending too many iterations.
 *
 *  Cheap to run: ~10 measureText calls in the worst case. Memoized
 *  per (content + bounds + font) in the caller. */
function computeFitFontSize(
  content: string,
  authoredSize: number,
  maxWidth: number,
  maxHeight: number | undefined,
  fontFamily: string,
  fontWeight: number | string,
  lineHeightMultiplier: number,
  maxLines: number | undefined,
  letterSpacing = 0,
): number {
  // When NEITHER a height bound NOR a maxLines cap is set, the element
  // is free to grow — no shrink needed. Return authored size as-is.
  if (!maxHeight && !maxLines) return authoredSize;
  const ctx = getMeasureCtx();
  if (!ctx) return authoredSize;
  const plain = stripHtmlForMeasurement(content);
  if (!plain.trim()) return authoredSize;

  const minSize = Math.max(8, Math.round(authoredSize * 0.4));
  let size = authoredSize;

  // Up to ~15 iterations before bailing out at minSize.
  for (let i = 0; i < 15; i++) {
    ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
    const lines = wrapToWidth(ctx, plain, maxWidth, letterSpacing);
    const renderedHeight = lines.length * size * lineHeightMultiplier;
    const fitsHeight = maxHeight ? renderedHeight <= maxHeight : true;
    const fitsLines = maxLines ? lines.length <= maxLines : true;
    if (fitsHeight && fitsLines) return size;
    if (size <= minSize) return minSize;
    size = Math.max(minSize, size * 0.92);
  }
  return Math.max(minSize, Math.round(size));
}

/** Analytical content height (px) of an element — text is measured by
 *  wrapping its resolved content; everything else uses its authored height.
 *  Used by the hug-chain reflow to position the next element below the
 *  previous one's ACTUAL content (not its fixed box). */
export function elementContentHeight(
  el: TemplateElement,
  slots: SocialPostSlots,
  preferStatic: boolean,
): number {
  if (el.type === 'text') {
    const raw = resolveContent(el.slotKey, el.staticText, slots, preferStatic);
    // Measure the text as it RENDERS: uppercase + letter-spacing both widen
    // it, so the wrap/line-count (hence the box height) must account for them.
    const content = el.uppercase ? raw.toUpperCase() : raw;
    const ls = el.letterSpacing ?? 0;
    const lh = el.lineHeight ?? 1.2;
    const size = computeFitFontSize(
      content, el.fontSize, el.width, undefined,
      el.fontFamily, el.fontWeight, lh, el.maxLines, ls,
    );
    const ctx = getMeasureCtx();
    let lines: number;
    if (ctx) {
      ctx.font = `${el.fontWeight} ${size}px ${el.fontFamily}`;
      lines = Math.max(1, wrapToWidth(ctx, stripHtmlForMeasurement(content), el.width, ls).length);
    } else {
      lines = Math.max(1, content.split(/\r?\n/).length);
    }
    return Math.ceil(lines * size * lh);
  }
  if ('height' in el && el.height) return el.height;
  if (el.type === 'button') return el.height ?? 60;
  return 0;
}

/** Resolve the effective Y of each element, honoring hug chains
 *  (stackOnPrevious). A chain = an anchor element followed by a run of
 *  stackOnPrevious elements; each is placed below the previous element's
 *  ACTUAL content bottom + its stackGap. Returns the resolved y per id +
 *  the set of TEXT element ids that should render auto-height (so they
 *  hug content instead of clipping to a fixed box). Elements outside any
 *  chain keep their authored y and are absent from autoHeight. */
export function resolveStackedPositions(
  elements: TemplateElement[],
  slots: SocialPostSlots,
  preferStatic: boolean,
): { y: Map<string, number>; autoHeight: Set<string> } {
  const y = new Map<string, number>();
  const autoHeight = new Set<string>();
  const n = elements.length;
  let i = 0;
  while (i < n) {
    let end = i;
    while (end + 1 < n && elements[end + 1].stackOnPrevious) end++;
    if (end > i) {
      let cy = elements[i].y;
      for (let j = i; j <= end; j++) {
        const el = elements[j];
        y.set(el.id, Math.round(cy));
        if (el.type === 'text') autoHeight.add(el.id); // text hugs; shapes keep height
        cy += elementContentHeight(el, slots, preferStatic);
        if (j + 1 <= end) cy += elements[j + 1].stackGap ?? 0;
      }
      i = end + 1;
    } else {
      y.set(elements[i].id, elements[i].y);
      i++;
    }
  }
  return { y, autoHeight };
}

function RenderText({ el, slots, preferStatic, fontsReady, inFlow }: {
  el: TextElement;
  slots: SocialPostSlots;
  preferStatic: boolean;
  fontsReady: boolean;
  inFlow: boolean;
}) {
  // In a stack the text hugs its content (no fixed height) and reflows;
  // maxLines is the growth cap. Absolutely placed → fixed-height box.
  const boxHeight = inFlow ? undefined : el.height;
  // Production: slot value wins, staticText is fallback (so AI fills
  // the real copy at compose time). Builder: staticText wins so the
  // user sees their typed preview text in the canvas instead of
  // SAMPLE_SLOTS data.
  const content = resolveContent(el.slotKey, el.staticText, slots, preferStatic);
  // Special-case the bullet_list slot: split on newlines and render
  // each line as a bulleted item. The renderer keeps the same
  // typography (font/size/color/etc.) — only the per-line bullet
  // prefix is added. Builder + production both render this way.
  const isBulletList = el.slotKey === 'bullet_list';

  // ----- Auto-shrink to fit -----
  // When the element has a height bound (and/or maxLines), measure the
  // wrapped content and pick the largest fontSize that fits. This
  // guarantees AI-filled text never overflows the authored bounds —
  // the author sized the element for their staticText placeholder; if
  // the AI returns longer copy, the renderer scales the type down
  // (floored at 40% of authored size). Same pattern the legacy preset
  // system uses (CLAUDE.md gotcha #25). For bullet_list, we measure
  // the longest single line — each bullet is its own li but we want
  // every bullet to render at the same size.
  const lineHeightMultiplier = el.lineHeight ?? 1.2;
  const rawMeasure = isBulletList
    ? content.split(/\r?\n/).map((l) => `• ${l.trim()}`).join('\n')
    : content;
  // Measure as RENDERED — uppercase + letter-spacing both widen the text,
  // so the wrap (and thus the auto-shrink) must account for them or the
  // measured line-count under-counts and the type won't shrink enough.
  const measurementSource = el.uppercase ? rawMeasure.toUpperCase() : rawMeasure;
  const measureLetterSpacing = el.letterSpacing ?? 0;
  const effectiveFontSize = useMemo(
    // Auto-shrink to honor maxLines / a height bound — for chain (inFlow) text
    // too: e.g. a maxLines=1 headline must shrink real copy to one line. This
    // is a FONT-SIZE decision and is safe — STACKING position is handled by the
    // CSS flow column (the subhead flows below the headline's ACTUAL rendered
    // height), so the only measurement here is the per-element font fit, which
    // is deterministic across the editor + headless bake once fonts load.
    // No-op when neither maxLines nor a height is set (early-returns authored).
    () => computeFitFontSize(
      measurementSource,
      el.fontSize,
      el.width,
      boxHeight,
      el.fontFamily,
      el.fontWeight,
      lineHeightMultiplier,
      el.maxLines,
      measureLetterSpacing,
    ),
    // fontsReady is in the deps so the fit RE-computes once web fonts
    // load — first pass (fallback metrics) may be wrong; second pass
    // (real fonts) is accurate.
    [measurementSource, el.fontSize, el.width, boxHeight,
     el.fontFamily, el.fontWeight, lineHeightMultiplier, el.maxLines,
     measureLetterSpacing, fontsReady],
  );

  // Vertical alignment within the box. Only meaningful for a fixed-height
  // (absolutely placed) box; in a stack the text hugs, so it's moot.
  const vAlign = el.verticalAlign ?? 'top';
  const useFlexVAlign = !!boxHeight && vAlign !== 'top';

  const containerStyle: React.CSSProperties = {
    position: inFlow ? 'relative' : 'absolute',
    ...(inFlow ? {} : { left: el.x, top: el.y }),
    width: el.width,
    height: boxHeight,
    fontFamily: el.fontFamily,
    fontSize: effectiveFontSize,
    fontWeight: el.fontWeight,
    color: el.color,
    lineHeight: el.lineHeight ?? 1.2,
    letterSpacing: el.letterSpacing,
    textAlign: el.textAlign ?? 'left',
    fontStyle: el.italic ? 'italic' : 'normal',
    textTransform: el.uppercase ? 'uppercase' : 'none',
    overflow: boxHeight ? 'hidden' : 'visible',
    opacity: el.opacity ?? 1,
    textShadow: textShadowCss(el.shadow),
    alignSelf: inFlow ? cssAlign(el.alignSelf) : undefined,
    ...(useFlexVAlign
      ? {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: vAlign === 'bottom' ? 'flex-end' : 'center',
        }
      : {}),
  };

  // Trust contract: allowHtml templates only have AI-generated content
  // matching a strict prompt format (e.g. a single <em>). Same pattern
  // PostEditorial uses. If we want defense-in-depth later, sanitize
  // with DOMPurify here.

  // Render as HTML when: (a) author explicitly opted in via allowHtml,
  // OR (b) the content has HTML-looking tags (AI fills frequently
  // include <em>...</em> per the suggest-copy prompt). This auto-detect
  // removes the manual "allowHtml" gotcha — JSON template authors don't
  // need to remember to flip the flag for AI-filled fields.
  const renderAsHtml = !!el.allowHtml || looksLikeHtml(content);

  // Shared style snippet for the bg-fill inner span. Three notes:
  //  - `lineHeight: 1` forces the line-box to hug the font's EM box.
  //    Without this override, the containerStyle's lineHeight (1.2-1.3)
  //    adds extra leading split top/bottom — but the bottom ALSO
  //    includes the font's descender region, which is unused space
  //    when the text is all-caps or has no descending glyphs. Net
  //    effect: visually more padding below than above. lineHeight:1
  //    collapses the leading so padding reads symmetric.
  //  - `verticalAlign: 'middle'` re-centers the (now-tight) inline
  //    span vertically against the containing line, since the
  //    containing block still has the authored lineHeight applied.
  //    Without this, the bg-pill appears top-aligned in the line-box.
  //  - Padding is uniform (changed from asymmetric in B-2.3).
  const bgPadding = el.backgroundPadding ?? 8;
  const bgInnerStyle: React.CSSProperties | undefined = el.backgroundColor
    ? {
        backgroundColor: el.backgroundColor,
        padding: `${bgPadding}px`,
        borderRadius: el.backgroundRadius ?? 0,
        lineHeight: 1,
        verticalAlign: 'middle',
        boxDecorationBreak: 'clone',
        WebkitBoxDecorationBreak: 'clone',
      }
    : undefined;

  // ----- Bullet list rendering -----
  // Split content on newlines; render each non-empty line as a bullet.
  // Background fill (if set) wraps each bullet line individually with
  // the same hug-text + box-decoration-break treatment as regular text.
  if (isBulletList) {
    const lines = content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    return (
      <ul data-eid={el.id} style={{ ...containerStyle, listStyle: 'none', margin: 0, padding: 0 }}>
        {lines.map((line, i) => {
          const lineContent = `• ${line}`;
          return (
            <li key={i} style={{ display: 'block' }}>
              {bgInnerStyle ? <span style={bgInnerStyle}>{lineContent}</span> : lineContent}
            </li>
          );
        })}
      </ul>
    );
  }

  // When backgroundColor is set, wrap the content in an inline <span>
  // so the fill HUGS the text rather than filling the full element
  // bounding box. `box-decoration-break: clone` makes wrapped lines
  // each get their own background (otherwise multi-line text would
  // get a single jagged rect spanning all lines).
  if (bgInnerStyle) {
    return (
      <div data-eid={el.id} style={containerStyle}>
        {renderAsHtml ? (
          <span style={bgInnerStyle} dangerouslySetInnerHTML={{ __html: content }} />
        ) : (
          <span style={bgInnerStyle}>{content}</span>
        )}
      </div>
    );
  }

  if (renderAsHtml) {
    return <div data-eid={el.id} style={containerStyle} dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return <div data-eid={el.id} style={containerStyle}>{content}</div>;
}

function RenderButton({ el, slots, preferStatic, inFlow }: {
  el: ButtonElement;
  slots: SocialPostSlots;
  preferStatic: boolean;
  inFlow: boolean;
}) {
  const label = resolveContent(el.slotKey, el.staticText, slots, preferStatic);
  const fullLabel = el.trailingChar ? `${label} ${el.trailingChar}` : label;
  const renderAsHtml = looksLikeHtml(fullLabel);
  const containerStyle: React.CSSProperties = {
    position: inFlow ? 'relative' : 'absolute',
    ...(inFlow ? { alignSelf: cssAlign(el.alignSelf) } : { left: el.x, top: el.y }),
    // hugWidth: the button hugs its label (fit-content) and grows up to
    // maxWidth for longer copy — never shrinks the font. Otherwise it's
    // a fixed-width box.
    width: el.hugWidth ? 'fit-content' : el.width,
    maxWidth: el.hugWidth ? el.maxWidth : undefined,
    minHeight: el.height,
    height: el.hugWidth ? 'auto' : el.height,
    padding:
      el.paddingX != null || el.paddingY != null
        ? `${el.paddingY ?? 0}px ${el.paddingX ?? 0}px`
        : undefined,
    boxSizing: 'border-box',
    // Outline variant: no fill, just a border in the (scene-derived) text color.
    backgroundColor: el.outline ? 'transparent' : el.backgroundColor,
    color: el.textColor,
    fontFamily: el.fontFamily,
    fontSize: el.fontSize,
    fontWeight: el.fontWeight,
    borderRadius: el.borderRadius,
    border: el.outline
      ? `${el.borderWidth ?? 2}px solid ${el.borderColor || el.textColor}`
      : (el.borderColor ? `${el.borderWidth ?? 1}px solid ${el.borderColor}` : undefined),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    // Allow wrapping so a label longer than maxWidth wraps within the
    // button rather than overflowing; short labels stay one line.
    whiteSpace: 'normal',
    opacity: el.opacity ?? 1,
    boxShadow: boxShadowCss(el.shadow),
  };
  if (renderAsHtml) {
    return <div data-eid={el.id} style={containerStyle} dangerouslySetInnerHTML={{ __html: fullLabel }} />;
  }
  return <div data-eid={el.id} style={containerStyle}>{fullLabel}</div>;
}

function RenderShape({ el, inFlow }: { el: ShapeElement; inFlow: boolean }) {
  const isCircle = el.shape === 'circle';
  // Stack drop shadow (outer) + inner shadow (inset) + a glass highlight edge.
  const shadows: string[] = [];
  const drop = boxShadowCss(el.shadow);
  if (drop) shadows.push(drop);
  const inner = boxShadowCss(el.innerShadow);
  if (inner) shadows.push(`inset ${inner}`);
  if (el.glass) shadows.push('inset 0 1px 1px rgba(255,255,255,0.35)');
  // Background blur from glass (default 12px) or an explicit backdropBlur.
  const backdrop = el.glass
    ? `blur(${el.backdropBlur ?? 12}px) saturate(1.3)`
    : (el.backdropBlur ? `blur(${el.backdropBlur}px)` : undefined);
  return (
    <div
      data-eid={el.id}
      style={{
        position: inFlow ? 'relative' : 'absolute',
        ...(inFlow ? { alignSelf: cssAlign(el.alignSelf) } : { left: el.x, top: el.y }),
        width: el.width,
        height: el.height,
        flex: 'none',
        // Gradient scrim (color → same hue at 0 alpha, no grey midpoint)
        // overrides the fill; else glass renders the fill translucent.
        backgroundColor: el.gradient
          ? 'transparent'
          : (el.glass ? hexWithAlpha(el.color, 0.18) : el.color),
        // Two-stop scrim: solid color up to gradientStart, then ramp to
        // transparent at gradientFade (both fractions of the rect from the
        // color edge). A wider start→fade gap = gentler slope = softer end.
        backgroundImage: el.gradient
          ? (() => {
              const start = Math.max(0, Math.min(0.95, el.gradientStart ?? 0));
              const end = Math.max(start + 0.05, Math.min(1, el.gradientFade ?? 1));
              return `linear-gradient(${el.gradient}, ${el.color} ${Math.round(start * 100)}%, ${hexWithAlpha(el.color, 0)} ${Math.round(end * 100)}%)`;
            })()
          : undefined,
        borderRadius: isCircle ? '50%' : (el.borderRadius ?? 0),
        opacity: el.opacity ?? 1,
        boxShadow: shadows.length ? shadows.join(', ') : undefined,
        filter: el.blur ? `blur(${el.blur}px)` : undefined,
        backdropFilter: backdrop,
        WebkitBackdropFilter: backdrop,
        border: el.glass ? '1px solid rgba(255,255,255,0.30)' : undefined,
      }}
    />
  );
}

/** Product slot — when productImageUrl is set, renders the user's
 *  image clipped to the slot. Otherwise shows fillColor as a
 *  placeholder. Behaves identically to other elements for layout
 *  purposes (position, opacity, border radius, rotation). */
function RenderProductSlot({ el, productImageUrl, inFlow }: {
  el: ProductSlotElement;
  productImageUrl: string;
  inFlow: boolean;
}) {
  return (
    <div
      data-eid={el.id}
      style={{
        position: inFlow ? 'relative' : 'absolute',
        ...(inFlow ? { alignSelf: cssAlign(el.alignSelf) } : { left: el.x, top: el.y }),
        width: el.width,
        height: el.height,
        flex: 'none',
        borderRadius: el.borderRadius ?? 0,
        opacity: el.opacity ?? 1,
        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        overflow: 'hidden',
        // Never paint the placeholder fill — an empty slot stays
        // transparent so the picker thumbnails + empty states keep the
        // template's aesthetic instead of showing a flat grey/white box.
        // (el.fillColor is retained in the schema for the visual builder.)
        backgroundColor: 'transparent',
        // No box-shadow: the product is a transparent cut-out floating in
        // the slot, so a rectangle shadow around the slot bounds would draw
        // a shadow around empty space. Any product shadow belongs in the
        // template's background design instead.
      }}
    >
      {productImageUrl && (
        <img
          src={productImageUrl}
          alt="Product"
          style={{
            width: '100%',
            height: '100%',
            // HARD RULE: the product image must always FIT inside its
            // slot — never crop, never overflow. `contain` scales the
            // whole product to fit (centered, letterboxed if aspect
            // ratios differ). We intentionally ignore el.objectFit here
            // so no template can opt into `cover` and clip the product.
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
          }}
        />
      )}
    </div>
  );
}

function RenderImage({ el, inFlow }: { el: ImageElement; inFlow: boolean }) {
  return (
    <img
      data-eid={el.id}
      src={el.src}
      alt=""
      style={{
        position: inFlow ? 'relative' : 'absolute',
        ...(inFlow ? { alignSelf: cssAlign(el.alignSelf) } : { left: el.x, top: el.y }),
        width: el.width,
        height: el.height,
        flex: 'none',
        objectFit: el.objectFit ?? 'cover',
        borderRadius: el.borderRadius ?? 0,
        opacity: el.opacity ?? 1,
        boxShadow: boxShadowCss(el.shadow),
        display: 'block',
      }}
    />
  );
}

/** Auto-layout container — lays children out in flow (flex) so the
 *  layout adapts to content length instead of leaving fixed gaps.
 *  Children render with inFlow=true (relative positioning). */
function RenderStack({ el, slots, productImageUrl, preferStatic, fontsReady, inFlow, brandLogoUrl }: {
  el: StackElement;
  slots: SocialPostSlots;
  productImageUrl: string;
  preferStatic: boolean;
  fontsReady: boolean;
  inFlow: boolean;
  brandLogoUrl: string | null;
}) {
  const style: React.CSSProperties = {
    position: inFlow ? 'relative' : 'absolute',
    ...(inFlow ? { alignSelf: cssAlign(el.alignSelf) } : { left: el.x, top: el.y }),
    width: el.width,
    height: el.height,
    flex: 'none',
    display: 'flex',
    flexDirection: el.direction === 'row' ? 'row' : 'column',
    gap: el.gap,
    paddingTop: el.paddingTop,
    paddingRight: el.paddingRight,
    paddingBottom: el.paddingBottom,
    paddingLeft: el.paddingLeft,
    alignItems: cssAlign(el.align) ?? 'flex-start',
    justifyContent: cssJustify(el.justify) ?? 'flex-start',
    backgroundColor: el.backgroundColor,
    borderRadius: el.borderRadius ?? 0,
    boxShadow: boxShadowCss(el.shadow),
    opacity: el.opacity ?? 1,
    boxSizing: 'border-box',
  };
  return (
    <div data-eid={el.id} style={style}>
      {el.children.map((c) =>
        renderElement(c, slots, productImageUrl, preferStatic, fontsReady, true, brandLogoUrl),
      )}
    </div>
  );
}

/** Logo slot — fills the region with the brand logo (fit, never cropped).
 *  Empty + transparent when the brand has no logo. Tint recolors the
 *  mark for contrast against dark/light scenes. */
function RenderLogoSlot({ el, brandLogoUrl, inFlow }: {
  el: LogoSlotElement;
  brandLogoUrl: string | null;
  inFlow: boolean;
}) {
  const tint = el.tint ?? 'original';
  const filter =
    tint === 'black'
      ? 'brightness(0)'
      : tint === 'white'
        ? 'brightness(0) invert(1)'
        : undefined;
  return (
    <div
      data-eid={el.id}
      style={{
        position: inFlow ? 'relative' : 'absolute',
        ...(inFlow ? { alignSelf: cssAlign(el.alignSelf) } : { left: el.x, top: el.y }),
        width: el.width,
        height: el.height,
        flex: 'none',
        borderRadius: el.borderRadius ?? 0,
        opacity: el.opacity ?? 1,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {brandLogoUrl && (
        <img
          src={brandLogoUrl}
          alt="Brand logo"
          crossOrigin="anonymous"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            filter,
            display: 'block',
          }}
        />
      )}
    </div>
  );
}
